import { Dialog } from '@base-ui/react/dialog';
import { useTranslation } from 'react-i18next';
import { InfoIcon, UnlinkIcon } from '@utils/icons';
import type { ISite } from '@interfaces/IProjectMerge';
import { Button } from '@components/ui/Button/Button';
import { ProvBadge } from '@components/ui/ProvBadge/ProvBadge';
import styles from './UnlinkModal.module.css';

interface IUnlinkModalProps {
  /** The site to delete, or null when the modal is closed. */
  site: ISite | null;
  genbaName: string;
  trusstorName: string;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (site: ISite) => void;
}

/** Confirmation modal for deleting a D-ORCA-owned site. Built on the Base UI
    Dialog primitive (same pattern as ui/DiscardModal). The copy is explicit
    that this is permanent: re-syncing mints a NEW site id, orphaning anything
    that referenced the old one. */
export function UnlinkModal({
  site,
  genbaName,
  trusstorName,
  loading = false,
  onCancel,
  onConfirm,
}: IUnlinkModalProps) {
  const { t } = useTranslation();
  return (
    <Dialog.Root
      open={!!site}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup className={styles.modal}>
          {site ? (
            <>
              <div className={styles.head}>
                <span className={styles.headIcon}>
                  <UnlinkIcon size={20} />
                </span>
                <div>
                  <Dialog.Title className={styles.title}>
                    {t('projectMerge.unlinkModal.title', { name: site.name })}
                  </Dialog.Title>
                  <Dialog.Description className={styles.desc}>
                    {t('projectMerge.unlinkModal.body')}
                  </Dialog.Description>
                </div>
              </div>

              <div className={styles.pair}>
                <ProvBadge provider="genba" />
                <span className={styles.pairEnd}>
                  <span className={`${styles.pairNm} ${styles.pairNmJp}`}>{genbaName}</span>
                  <span className={styles.pairCd}>{site.genbaSiteNumber}</span>
                </span>
                <span className={styles.pairIcon}>
                  <UnlinkIcon size={16} />
                </span>
                <span className={`${styles.pairEnd} ${styles.pairEndRight}`}>
                  <span className={styles.pairNm}>{trusstorName}</span>
                  <span className={styles.pairCd}>{site.trusstorProjectId}</span>
                </span>
                <ProvBadge provider="trusstor" />
              </div>

              <div className={styles.note}>
                <InfoIcon size={14} />
                <span>{t('projectMerge.unlinkModal.note', { siteId: site.siteId })}</span>
              </div>

              <div className={styles.foot}>
                <Button variant="ghost" size="md" onClick={onCancel}>
                  {t('projectMerge.unlinkModal.cancel')}
                </Button>
                <Button variant="dangerSolid" size="md" disabled={loading} onClick={() => onConfirm(site)}>
                  <UnlinkIcon size={15} />
                  {t('projectMerge.unlinkModal.confirm')}
                </Button>
              </div>
            </>
          ) : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
